import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-spend.js

/**
 * SpendHandler
 * 
 * Manages spend volume data for carbon accounting:
 * - API integration for retrieving and manipulating spend data
 * - CRUD operations with validation and error handling
 * - Data transformation between API and UI formats
 * - Real-time updates via WebSocket connection
 * - Integration with emissions recalculation workflow
 */
export class SpendHandler {
  /**
   * Initialize the spend handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;

    // Register with WebSocketHandler if available
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'SpendHandler',
        this.handleUpdate.bind(this),
        [
          'spend',
          'spend:update',
          'spend-volume',
          'spend-volume:update'
        ],
        this
      );
    }
  }

  /**
   * Load all spend volume data from Strapi
   * @returns {Promise<Object>} Spend volume data grouped by year
   * @throws {Error} If API request fails
   */
  async loadSpendVolume() {
    try {
      this.store.state.loading.spendVolume = true;
      this.store.notifySubscribers('loading');
      const projectId = this.store.getProjectId();

      // Early return if no project ID is available
      if (!projectId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cannot load spend volume: No project ID available');
        }
        return {};
      }

      const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch spend volume data: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const { data: rawData } = await response.json();

      // Transform and group by year
      const byYear = this.processRawSpendVolumeData(rawData);

      // Update store with simplified metadata
      this.store.state.data.spendVolume = {
        byYear,
        meta: {
          lastSync: new Date().toISOString()
        }
      };

      this.store.notifySubscribers('spendVolume');
      return byYear;
    } catch (error) {
      console.error('Spend volume data load failed:', error);
      this.store.handleError('spendVolume', error);
      return {};
    } finally {
      this.store.state.loading.spendVolume = false;
      this.store.notifySubscribers('loading');
    }
  }

  /**
   * Process raw spend volume data from API into year-based structure
   * @private
   * @param {Array} rawData - Raw data from API
   * @returns {Object} Processed data grouped by year
   */
  processRawSpendVolumeData(rawData) {
    if (!Array.isArray(rawData)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Invalid spend volume data received:', rawData);
      }
      return {};
    }

    return rawData.reduce((acc, item) => {
      const data = item.attributes || item;  // Handle Strapi's data structure
      const year = data.Year?.toString();

      if (!year) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Skipping spend volume item without year:', item);
        }
        return acc;
      }

      if (!acc[year]) {
        acc[year] = [];
      }

      acc[year].push({
        id: item.id,
        year: data.Year?.toString(),
        snowflakeId: data.SnowflakeId || '',
        resourceId: data.ResourceId || '',
        turnoverInThsdUSD: parseFloatSafe(data.TurnoverInThsdUSD),
        spendShareOfTurnover: parseFloatSafe(data.SpendShareOfTurnover)
      });

      return acc;
    }, {});
  }

  /**
   * Get the most recent spend volume record for a year
   * @param {string} year - The year to get data for
   * @returns {Object|null} Most recent record or null if none exists
   */
  getMostRecentSpendVolume(year) {
    const yearData = this.store.state.data.spendVolume.byYear[year] || [];
    if (!yearData.length) return null;

    // Sort by ID (assuming higher IDs are more recent) and return most recent
    return [...yearData].sort((a, b) => parseInt(b.id) - parseInt(a.id))[0];
  }

  /**
   * Update turnover value by creating a new record or updating existing one
   * Preserves the most recent spend share value
   * @param {string} year - Year to update
   * @param {number|string} turnover - New turnover value in thousands USD
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async updateTurnover(year, turnover) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to update turnover');
      }

      if (!year) {
        throw new Error('Year is required to update turnover');
      }

      const numTurnover = parseFloatSafe(turnover);
      if (numTurnover < 0) {
        throw new Error('Turnover must be a non-negative number');
      }

      const currentRecord = this.getMostRecentSpendVolume(year);

      // Create new if doesn't exist
      if (!currentRecord) {
        return this.createSpendVolume({
          Year: year,
          SpendShareOfTurnover: '0',
          TurnoverInThsdUSD: numTurnover.toString()
        });
      }

      // Update existing record
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${currentRecord.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              TurnoverInThsdUSD: numTurnover.toString()
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to update turnover: ${response.statusText}`);
      }

      // Sync with Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${currentRecord.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Turnover updated but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // Flag for recalculation using shared utility
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      // Update data
      await this.loadSpendVolume();

      // Notify emissions handler
      this.store.emissionsHandler.handleUpdate({
        action: 'update',
        method: 'updateTurnover',
        year: year,
        timestamp: new Date().toISOString(),
        source: 'direct',
        scope: 'scope3',
        details: {
          id: currentRecord.id,
          turnover: numTurnover
        }
      });

      // Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to update turnover:', error);
      this.store.handleError('spendVolume', error);
      throw error;
    }
  }

  /**
   * Update spend share value
   * @param {string} year - Year to update
   * @param {number|string} spendShare - New spend share value (percentage)
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async updateSpendShare(year, spendShare) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to update spend share');
      }

      if (!year) {
        throw new Error('Year is required to update spend share');
      }

      const numSpendShare = parseFloatSafe(spendShare);
      if (numSpendShare < 0 || numSpendShare > 100) {
        throw new Error('Spend share must be between 0 and 100 percent');
      }

      const currentRecord = this.getMostRecentSpendVolume(year);

      if (!currentRecord) {
        return this.createSpendVolume({
          Year: year,
          SpendShareOfTurnover: numSpendShare.toString(),
          TurnoverInThsdUSD: '0'
        });
      }

      // Update the existing record
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${currentRecord.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              SpendShareOfTurnover: numSpendShare.toString()
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to update spend share: ${response.statusText}`);
      }

      // Sync with Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${currentRecord.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Spend share updated but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // Flag for recalculation using shared utility
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      // Update data
      await this.loadSpendVolume();

      // Notify emissions handler
      this.store.emissionsHandler.handleUpdate({
        action: 'update',
        method: 'updateSpendShare',
        year: year,
        timestamp: new Date().toISOString(),
        source: 'direct',
        scope: 'scope3',
        details: {
          id: currentRecord.id,
          spendShare: numSpendShare
        }
      });

      // Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to update spend share:', error);
      this.store.handleError('spendVolume', error);
      throw error;
    }
  }

  /**
   * Create a new spend volume record
   * @param {Object} data - Spend volume data
   * @param {string} data.Year - Year for the new record
   * @param {string} [data.SpendShareOfTurnover] - Spend share as percentage
   * @param {string} [data.TurnoverInThsdUSD] - Turnover in thousands USD
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async createSpendVolume(data) {
    try {
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to create spend volume');
      }

      if (!data.Year) {
        throw new Error('Year is required to create spend volume');
      }

      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({ data })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Create failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Sync with Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${result.data.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Spend volume created but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // Flag for recalculation using shared utility
      await this.store.emissionsHandler.flagYearForRecalculation(data.Year);

      // Reload data
      await this.loadSpendVolume();

      // Notify emissions handler
      this.store.emissionsHandler.handleUpdate({
        action: 'create',
        method: 'createSpendVolume',
        year: data.Year,
        timestamp: new Date().toISOString(),
        source: 'direct',
        scope: 'scope3',
        details: {
          id: result.data.id
        }
      });

      // Save state
      await this.store.saveState();

      return true;
    } catch (error) {
      console.error('Failed to create spend volume:', error);
      this.store.handleError('spendVolume', error);
      throw error;
    }
  }

  /**
   * Delete a spend volume record
   * @param {string} documentId - Document ID to delete
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async deleteSpendRecord(documentId) {
    try {
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to delete spend volume');
      }

      if (!documentId) {
        throw new Error('Document ID is required to delete spend volume');
      }

      // Get year info for the record being deleted
      let year = null;
      try {
        // Find the year for the record being deleted
        const allYears = this.store.state.data.spendVolume.byYear || {};
        for (const [yearKey, records] of Object.entries(allYears)) {
          const found = records.find(r => r.id === documentId);
          if (found) {
            year = yearKey;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding year for deleted record:', err);
        }
      }

      const deleteUrl = `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/${documentId}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to delete spend volume: ${response.statusText}`);
      }

      // Sync deletion with Snowflake
      const syncUrl = `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/cleanup`;
      const syncResponse = await fetch(syncUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Spend volume deleted but Snowflake cleanup failed. Will retry on next calculation.');
        }
      }

      // Flag for recalculation using shared utility if we have the year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // Force reload data after delete
      await this.loadSpendVolume();

      // Notify emissions handler
      if (year) {
        this.store.emissionsHandler.handleUpdate({
          action: 'delete',
          method: 'deleteSpendVolume',
          year: year,
          id: documentId,
          timestamp: new Date().toISOString(),
          source: 'direct',
          scope: 'scope3',
          details: {}
        });
      }

      // Save state
      await this.store.saveState();

      return true;

    } catch (error) {
      console.error('Failed to delete spend volume:', error);
      this.store.handleError('spendVolume', error);
      throw error;
    }
  }

  /**
   * Handle real-time updates from WebSocket
   * @param {Object} data - Update data from WebSocket
   * @returns {Promise<void>}
   */
  async handleUpdate(data) {
    try {
      // Simply reload the data
      await this.loadSpendVolume();

      // Extract year from data for WebSocket updates
      const year = data.year || data.Year ||
        (data.data && data.data.Year ? data.data.Year : null);

      // Flag for recalculation using shared utility if we have the year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // Save state
      await this.store.saveState();

      this.store.notifySubscribers('syncStatus', {
        type: 'success',
        message: year
          ? `Spend volume data updated for year ${year}`
          : `Spend volume data updated successfully`,
        timestamp: new Date(),
        details: {
          updatedAt: new Date().toISOString(),
          source: 'websocket',
          year: year
        }
      });
    } catch (error) {
      console.error('Failed to reload spend volume data:', error);
      this.store.handleError('spendVolume', {
        message: error.message,
        details: {
          timestamp: new Date().toISOString(),
          source: 'websocket',
          originalError: error
        }
      });
    }
  }
}

/**
 * Safely parse float with fallback to 0
 * @param {string|number} value - Value to parse
 * @returns {number} Parsed float or 0 if invalid
 */
function parseFloatSafe(value) {
  if (value === null || value === undefined) return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}